import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { X, FileText, Loader2, Building2, CheckCircle2, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inp = "w-full rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-4 py-3 font-body text-[#1F2937] outline-none focus:border-[#0D9488]";

function money(cents) {
  if (!cents) return "$0.00";
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Modal that lets a school / district admin submit a purchase-order request
// instead of paying by card. Use for large multi-school or district contracts
// where card processors reject the charge amount.
export default function PurchaseOrderModal({ open, onClose, defaultOrg, onSubmitted }) {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    plan: "", seats: 100, po_number: "",
    billing_email: "", billing_name: "", organization: defaultOrg || "",
    billing_phone: "", billing_address: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.getPlans().then((d) => {
      const list = (d.plans || []).filter((p) => p.active);
      setPlans(list);
      // Pre-select the highest-tier plan (district > individual_school > ...)
      const preferred = list.find((p) => p.plan === "district") || list.find((p) => p.plan === "individual_school") || list[0];
      if (preferred) setForm((f) => ({ ...f, plan: preferred.plan }));
    }).catch(() => {});
    setForm((f) => ({ ...f, organization: defaultOrg || f.organization }));
    setDone(null);
  }, [open, defaultOrg]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const activePlan = useMemo(() => plans.find((p) => p.plan === form.plan), [plans, form.plan]);
  const annualPrice = useMemo(() => {
    if (!activePlan) return 0;
    const p = (activePlan.prices || []).find((x) => x.interval === "year") || (activePlan.prices || [])[0];
    return p ? p.amount : 0;
  }, [activePlan]);
  // For district/individual_school plans "seats" is teachers; we treat estimated_amount = annualPrice * seats as a hint.
  const estimateCents = useMemo(() => {
    if (!activePlan) return 0;
    // If plan already covers many teachers (max_students * students-per-teacher), quote the plan price once.
    // Otherwise scale per teacher-seat.
    const maxIncluded = activePlan.max_students || 1;
    if (maxIncluded >= form.seats) return annualPrice;
    return Math.round(annualPrice * (form.seats / maxIncluded));
  }, [activePlan, annualPrice, form.seats]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.submitPurchaseOrder({ ...form, estimated_amount_cents: estimateCents });
      setDone(r);
      toast.success("Purchase order submitted");
      onSubmitted && onSubmitted(r);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#1F2937]/60 p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="po-modal">
          <motion.div initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
            className="w-full max-w-xl rounded-[28px] bg-white p-7 shadow-2xl border border-[#E5DECF] overflow-y-auto max-h-[90vh]">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#7A3FF2]/10 text-[#7A3FF2]"><FileText size={22} /></div>
                <div>
                  <h3 className="font-heading text-xl font-extrabold text-[#1F2937]">Pay by purchase order</h3>
                  <p className="font-body text-xs text-[#6B7280]">For districts and schools that can't process a card charge that large.</p>
                </div>
              </div>
              <button onClick={onClose} className="rounded-xl p-1.5 text-[#6B7280] hover:bg-[#F2ECE1]" data-testid="po-close"><X size={20} /></button>
            </div>

            {done ? (
              <div className="mt-6 rounded-2xl border-2 border-[#10B981]/40 bg-[#10B981]/10 p-5" data-testid="po-success">
                <div className="flex items-center gap-2 font-heading text-lg font-bold text-[#047857]"><CheckCircle2 size={18} /> PO request submitted</div>
                <p className="mt-2 font-body text-sm text-[#047857]/90">Our billing team will email an invoice to <strong>{done.billing_email}</strong> within one business day. Your plan turns on the moment the invoice is marked paid.</p>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><div className="font-bold text-[#1F2937]">Plan</div><div>{done.plan_name} · {done.seats} seats</div></div>
                  <div><div className="font-bold text-[#1F2937]">Est. amount</div><div>{money(done.amount_cents)}</div></div>
                  <div><div className="font-bold text-[#1F2937]">PO #</div><div>{done.po_number || "—"}</div></div>
                  <div><div className="font-bold text-[#1F2937]">Status</div><div className="capitalize">{done.status}</div></div>
                </div>
                <button onClick={onClose} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0D9488] px-5 py-2 font-bold text-white hover:bg-[#0B7F74]" data-testid="po-success-close">Done</button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="rounded-2xl bg-[#F2ECE1] px-3 py-2 text-xs font-semibold text-[#6B7280] flex items-start gap-2">
                  <Info size={14} className="mt-0.5 text-[#0D9488]" />
                  Card processors reject charges over roughly $40k. Submit a PO and our billing team will email you an invoice with wire / ACH / check payment instructions.
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-sm font-bold">Plan
                    <select value={form.plan} onChange={(e) => set("plan", e.target.value)} data-testid="po-plan" className={`${inp} mt-1`} required>
                      <option value="" disabled>Choose plan…</option>
                      {plans.map((p) => (<option key={p.plan} value={p.plan}>{p.name}</option>))}
                    </select>
                  </label>
                  <label className="text-sm font-bold">Seats needed
                    <input type="number" min={1} value={form.seats} onChange={(e) => set("seats", parseInt(e.target.value || "1", 10))} data-testid="po-seats" className={`${inp} mt-1`} required />
                  </label>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-sm font-bold sm:col-span-2 flex items-center gap-2"><Building2 size={14} /> Organization
                    <input value={form.organization} onChange={(e) => set("organization", e.target.value)} data-testid="po-organization" className={`${inp} mt-1`} required placeholder="Sunrise Unified School District" />
                  </label>
                  <label className="text-sm font-bold">Billing contact name
                    <input value={form.billing_name} onChange={(e) => set("billing_name", e.target.value)} data-testid="po-billing-name" className={`${inp} mt-1`} required />
                  </label>
                  <label className="text-sm font-bold">Billing email
                    <input type="email" value={form.billing_email} onChange={(e) => set("billing_email", e.target.value)} data-testid="po-billing-email" className={`${inp} mt-1`} required />
                  </label>
                  <label className="text-sm font-bold">Billing phone
                    <input value={form.billing_phone} onChange={(e) => set("billing_phone", e.target.value)} data-testid="po-billing-phone" className={`${inp} mt-1`} placeholder="Optional" />
                  </label>
                  <label className="text-sm font-bold">Your PO #
                    <input value={form.po_number} onChange={(e) => set("po_number", e.target.value)} data-testid="po-number" className={`${inp} mt-1`} placeholder="Leave blank if not issued yet" />
                  </label>
                  <label className="text-sm font-bold sm:col-span-2">Billing address
                    <textarea value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} data-testid="po-address" className={`${inp} mt-1 h-20`} placeholder="Street, City, State, ZIP" />
                  </label>
                  <label className="text-sm font-bold sm:col-span-2">Notes for our billing team
                    <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="po-notes" className={`${inp} mt-1 h-20`} placeholder="Anything we should know? Renewal date, requisition form, etc." />
                  </label>
                </div>
                <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-[#6B7280]">Estimated invoice</div>
                    <div className="font-heading text-xl font-extrabold text-[#0D9488]" data-testid="po-estimate">{money(estimateCents)}</div>
                    <div className="font-body text-xs text-[#6B7280]">Final invoice reflects negotiated pricing and any district discounts.</div>
                  </div>
                  <button type="submit" disabled={busy || !form.plan} data-testid="po-submit"
                    className="inline-flex items-center gap-2 rounded-full bg-[#0D9488] px-6 py-3 font-heading font-bold text-white hover:bg-[#0B7F74] disabled:opacity-50">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} Submit PO request
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
